/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from 'react';

// porp-types is a library for typechecking of props
import PropTypes from 'prop-types';

// react-chartjs-2 components
import { Pie } from 'react-chartjs-2';

// @mui material components
import Card from '@mui/material/Card';

// Soft UI Dashboard PRO React components
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';

// PieChart configurations
import configs from 'prefabsComponents/Charts/PieChart/configs';

import SoftButton from 'components/SoftButton';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

function PieChart({ title, description, height, chart, children }) {
    const { data, options } = configs(chart.labels || [], chart.datasets || {});

    const renderChart = (
        <SoftBox p={2}>
            {title || description ? (
                <SoftBox px={description ? 1 : 0} pt={description ? 1 : 0}>
                    {title && (
                        <SoftBox
                            mb={1}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <SoftTypography variant="h6">
                                {title}
                            </SoftTypography>
                            <SoftButton variant="outlined" color="dark">
                                <CloudDownloadIcon />
                                &nbsp;Descargar
                            </SoftButton>
                        </SoftBox>
                    )}
                    <SoftBox mb={2}>
                        <SoftTypography
                            component="div"
                            variant="button"
                            fontWeight="regular"
                            color="text"
                        >
                            {description}
                        </SoftTypography>
                    </SoftBox>
                </SoftBox>
            ) : null}
            {useMemo(
                () => (
                    <SoftBox height={height}>
                        <Pie data={data} options={options} />
                    </SoftBox>
                ),
                [chart, height]
            )}
            {children && <SoftBox mt={2}>{children}</SoftBox>}
        </SoftBox>
    );

    return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of PieChart
PieChart.defaultProps = {
    title: '',
    children: null,
    description: '',
    height: '19.125rem',
};

// Typechecking props for the PieChart
PieChart.propTypes = {
    title: PropTypes.string,
    children: PropTypes.node,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    chart: PropTypes.objectOf(
        PropTypes.oneOfType([PropTypes.array, PropTypes.object])
    ).isRequired,
};

export default PieChart;
